import React, { FC, useContext, useEffect, useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';
import { api, endpoints } from '../../api';
import LkFooterComponent from '../../components/LkFooterComponent';
import LkNavbar from '../../components/LkNavbarComponent';
import LkSidebarComponent from '../../components/LkSidebarComponent';
import NavbarComponent from '../../components/NavbarComponent';
import { NeutralNavbarComponent } from '../../components/NeutralNavbarComponent/NeutralNavbarComponent';
import { login, logout } from '../../store/authStore/auth.action';
import { authContext } from '../../store/authStore/AuthContext';
import { MainpageComponentContainer, MainpageContainer } from '../../styles/styled';
import { UserDataResponseType } from '../../types';
import { routerPath } from '../routerPath';
import { expertRoutes } from './components/ExpertRoutes/ExpertRoutes';
import { lkRoutes } from './components/LkRoutes/LkRoutes';
import { LkContainerUI, LkNavbarOthersContainer } from './components/LkRoutes/styled';
import { mainpageRoutes, representativeRegisterRoute } from './components/MainpageRoutes/MainpageRoutes';
import { nmsRoutes } from './components/NmsRoutes/NmsRoutes';
import { povsRoutes } from './components/PovsRoutes/PovsRoutes';
import { representativeRoutes } from './components/RepresentativeRoutes/RepresentativeRoutes';

export const RoutesComponent: FC = () => {
  const { state, dispatch } = useContext(authContext);

  useEffect(() => {
    if (
      state.token &&
      !state.isNms &&
      !state.isFederal &&
      !state.isRepresentative &&
      !state.isExpert &&
      !state.isPovs &&
      !state.isSuperFederal
    ) {
      (async () => {
        try {
          const response = await api.get<UserDataResponseType>(endpoints.getUserData());

          if (response.status === 200) {
            dispatch(login(state.token, response.data.roles));
          } else {
            dispatch(logout());
          }
        } catch (err) {
          dispatch(logout());
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token]);

  const mainpageRoutesGenerator = useMemo(() => {
    return mainpageRoutes.map((el, key) => (
      <Route
        path={el.path}
        key={key}
        element={
          <div style={{ width: '100%' }}>
            <NavbarComponent />

            {el.path !== routerPath.home && <NeutralNavbarComponent />}
            {el.path !== routerPath.home ? (
              <MainpageComponentContainer>
                <Breadcrumb className="breadcrumbs">
                  {mainpageRoutes
                    .filter((route) => el.path.match(route.path))
                    .map((el, key) => {
                      return (
                        <Breadcrumb.Item key={key}>
                          <Link to={el.path}>{el.name}</Link>
                        </Breadcrumb.Item>
                      );
                    })}
                </Breadcrumb>
                {el.Component}
              </MainpageComponentContainer>
            ) : (
              el.Component
            )}
          </div>
        }
      />
    ));
  }, []);

  const routesRender = useMemo(() => {
    if (!state.token && !state.isFederal && !state.isRepresentative && !state.isExpert && !state.isPovs) {
      return (
        <MainpageContainer>
          <Routes>
            {mainpageRoutesGenerator}
            {mainpageRoutes.map((el) => (
              <Route
                path={el.path}
                key={el.path}
                element={
                  <div style={{ width: '100%' }}>
                    <NavbarComponent />
                    <NeutralNavbarComponent />
                    <MainpageComponentContainer>{el.Component}</MainpageComponentContainer>
                  </div>
                }
              />
            ))}
            {representativeRegisterRoute.map((el) => (
              <Route
                path={el.path}
                key={el.path}
                element={
                  <div style={{ width: '100%' }}>
                    <NavbarComponent />
                    <NeutralNavbarComponent />
                    <MainpageComponentContainer>{el.Component}</MainpageComponentContainer>
                  </div>
                }
              />
            ))}
            <Route path="*" element={<Navigate to={routerPath.home} replace />} />
          </Routes>
        </MainpageContainer>
      );
    }

    if (state.isRepresentative) {
      return (
        <Routes>
          {mainpageRoutesGenerator}
          {representativeRoutes.map(({ path, Component }, index) => (
            <Route
              path={path}
              key={index}
              element={
                <>
                  <LkContainerUI>
                    <LkSidebarComponent />
                    <LkNavbarOthersContainer>
                      <LkNavbar />
                      <Breadcrumb className="breadcrumbs">
                        <Breadcrumb.Item>
                          <Link to={routerPath.home}>Главная</Link>
                        </Breadcrumb.Item>
                        {representativeRoutes
                          .filter((route) => path.match(route.path))
                          .map((el, key) => {
                            return (
                              <Breadcrumb.Item key={key}>
                                <Link to={el.path}>{el.name}</Link>
                              </Breadcrumb.Item>
                            );
                          })}
                      </Breadcrumb>
                      {Component}
                      <LkFooterComponent />
                    </LkNavbarOthersContainer>
                  </LkContainerUI>
                </>
              }
            />
          ))}
          <Route path="*" element={<Navigate to={routerPath.representativeProfile} replace />} />
        </Routes>
      );
    }

    if (state.isPovs) {
      return (
        <Routes>
          {mainpageRoutesGenerator}
          {povsRoutes.map(({ path, Component }, index) => (
            <Route
              path={path}
              key={index}
              element={
                <>
                  <LkContainerUI>
                    <LkSidebarComponent />
                    <LkNavbarOthersContainer>
                      <LkNavbar />
                      <Breadcrumb className="breadcrumbs">
                        <Breadcrumb.Item>
                          <Link to={routerPath.home}>Главная</Link>
                        </Breadcrumb.Item>
                        {povsRoutes
                          .filter((route) => path.match(route.path))
                          .map((el, key) => {
                            return (
                              <Breadcrumb.Item key={key}>
                                <Link to={el.path}>{el.name}</Link>
                              </Breadcrumb.Item>
                            );
                          })}
                      </Breadcrumb>
                      {Component}
                      <LkFooterComponent />
                    </LkNavbarOthersContainer>
                  </LkContainerUI>
                </>
              }
            />
          ))}
          <Route path="*" element={<Navigate to={routerPath.povsLk} replace />} />
        </Routes>
      );
    }

    if (state.isNms) {
      return (
        <Routes>
          {mainpageRoutesGenerator}
          {nmsRoutes.map(({ path, Component }, index) => (
            <Route
              path={path}
              key={index}
              element={
                <>
                  <LkContainerUI>
                    <LkSidebarComponent />
                    <LkNavbarOthersContainer>
                      <LkNavbar />
                      <Breadcrumb className="breadcrumbs">
                        <Breadcrumb.Item>
                          <Link to={routerPath.home}>Главная</Link>
                        </Breadcrumb.Item>
                        {nmsRoutes
                          .filter((route) => path.match(route.path))
                          .map((el, key) => {
                            return (
                              <Breadcrumb.Item key={key}>
                                <Link to={el.path}>{el.name}</Link>
                              </Breadcrumb.Item>
                            );
                          })}
                      </Breadcrumb>
                      {Component}
                      <LkFooterComponent />
                    </LkNavbarOthersContainer>
                  </LkContainerUI>
                </>
              }
            />
          ))}
          <Route path="*" element={<Navigate to={routerPath.nmsProfile} replace />} />
        </Routes>
      );
    }

    if (state.isExpert) {
      return (
        <Routes>
          {mainpageRoutesGenerator}
          {expertRoutes.map(({ path, Component }, index) => (
            <Route
              path={path}
              key={index}
              element={
                <>
                  <LkContainerUI>
                    <LkSidebarComponent />
                    <LkNavbarOthersContainer>
                      <LkNavbar />
                      <Breadcrumb className="breadcrumbs">
                        <Breadcrumb.Item>
                          <Link to={routerPath.home}>Главная</Link>
                        </Breadcrumb.Item>
                        {expertRoutes
                          .filter((route) => path.match(route.path))
                          .map((el, key) => {
                            return (
                              <Breadcrumb.Item key={key}>
                                <Link to={el.path}>{el.name}</Link>
                              </Breadcrumb.Item>
                            );
                          })}
                      </Breadcrumb>
                      {Component}
                      <LkFooterComponent />
                    </LkNavbarOthersContainer>
                  </LkContainerUI>
                </>
              }
            />
          ))}
          <Route path="*" element={<Navigate to={routerPath.expertUserProfile} replace />} />
        </Routes>
      );
    }

    if (state.isFederal || state.isSuperFederal)
      return (
        <Routes>
          {mainpageRoutesGenerator}
          {lkRoutes.map(({ path, Component }, index) => (
            <Route
              path={path}
              key={index}
              element={
                <LkContainerUI>
                  <LkSidebarComponent />
                  <LkNavbarOthersContainer>
                    <LkNavbar />
                    <Breadcrumb className="breadcrumbs">
                      <Breadcrumb.Item>
                        <Link to={routerPath.home}>Главная</Link>
                      </Breadcrumb.Item>
                      {lkRoutes
                        .filter((route) => path.match(route.path))
                        .map((el, key) => {
                          return (
                            <Breadcrumb.Item key={key}>
                              <Link to={el.path}>{el.name}</Link>
                            </Breadcrumb.Item>
                          );
                        })}
                    </Breadcrumb>
                    {Component}
                    <LkFooterComponent />
                  </LkNavbarOthersContainer>
                </LkContainerUI>
              }
            />
          ))}
          <Route path="*" element={<Navigate to={routerPath.application} replace />} />
        </Routes>
      );
    // eslint-disable-next-line
  }, [state]);

  return <BrowserRouter> {routesRender} </BrowserRouter>;
};
